// DS: MO3.4.2
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import OriginalBrush from '@wix/da-ds/pkg/Icons/16x16/OriginalBrush';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import DeviationShopCard, { type DeviationShopCardProps } from '../DeviationShopCard';
import { formatDollars } from '../../utils/formatting';

import s from './AncShopCard.scss';

export interface Props extends Pick<DeviationShopCardProps, 'allowedIndicators'> {
  deviation: PapiDeviation;
  width: number;
  withAuthorDetails?: boolean;
  withDescriptionBlock?: boolean;
  completedOrdersCount?: number;
  /** When false, the "Explore Original" footer CTA is hidden. Default true. */
  showFooterCta?: boolean;
}

const AncShopCard: React.FC<Props> = ({
  width,
  deviation,
  withAuthorDetails,
  allowedIndicators,
  withDescriptionBlock = false,
  completedOrdersCount: completedOrdersCountProp,
  showFooterCta = true,
}) => {
  const { t } = useTranslation();
  const anc = deviation.anc || deviation.extended?.anc;
  const completedOrdersCount = completedOrdersCountProp ?? 0;

  const belowCoverImageRightSlot =
    anc &&
    (completedOrdersCount === 0 ? (
      <div className={s['orders-count']}>{t('shop.thumb.anc.makeFirstOrder')}</div>
    ) : (
      <div className={s['orders-count']}>
        {t('shop.thumb.anc.totalOrders', {
          count: completedOrdersCount,
        })}
      </div>
    ));

  const detailsRightSlot = anc && (
    <Flex direction="column" className={s['details']}>
      <div className={s['base-price']}>
        <div className={s['base-price-dollars']}>{formatDollars(anc.basePrice)}</div>
        {t('shop.thumb.anc.basePrice')}
      </div>
    </Flex>
  );

  return (
    <DeviationShopCard
      width={width}
      deviation={deviation}
      withStats={true}
      withCommentCount={true}
      withDescriptionBlock={withDescriptionBlock}
      withAuthorDetails={withAuthorDetails}
      allowedIndicators={allowedIndicators}
      belowCoverImageRightSlot={belowCoverImageRightSlot}
      detailsRightSlot={detailsRightSlot}
      footerSlot={
        showFooterCta ? (
          <BuyButton
            size="medium"
            variant="outline"
            rounded
            fillContainerWidth
            icon={OriginalBrush}
          >
            {t('shop.thumbs.anc.explore')}
          </BuyButton>
        ) : undefined
      }
    />
  );
};
AncShopCard.displayName = 'AncShopCard';

export default AncShopCard;
